<template>
  <div
    v-if="receipt"
    class="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-md shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-300">
        <div class="text-sm">
          <div class="font-semibold">{{ receipt.original_filename || `Receipt #${receipt.id}` }}</div>
          <div class="text-xs text-gray-500">
            {{ receipt.content_type || 'unknown' }} · {{ formatBytes(receipt.size_bytes) }}
          </div>
        </div>
        <div class="flex items-center gap-2">
          <a
            v-if="receipt.file_url"
            :href="receipt.file_url"
            target="_blank"
            class="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100"
          >Open in new tab</a>
          <button
            class="text-xs px-2 py-1 border border-gray-400 rounded hover:bg-gray-100"
            @click="$emit('close')"
          >Close</button>
        </div>
      </div>

      <div class="flex-1 overflow-auto bg-gray-100 flex items-center justify-center">
        <img
          v-if="isImage"
          :src="receipt.file_url"
          :alt="receipt.original_filename || 'Receipt'"
          class="max-w-full max-h-[80vh] object-contain"
          @error="imgError = true"
        />
        <div v-else-if="isPdf" class="w-full h-[80vh]">
          <iframe
            :src="receipt.file_url"
            class="w-full h-full border-0"
            :title="receipt.original_filename || 'Receipt PDF'"
          ></iframe>
        </div>
        <div v-else class="p-8 text-center text-gray-600 text-sm">
          <div>Preview not available for this file type.</div>
          <div class="mt-2 text-xs">Use "Open in new tab" to view.</div>
        </div>
        <div
          v-if="imgError"
          class="absolute p-6 text-center text-red-700 text-sm bg-red-50 rounded border border-red-200"
        >
          Failed to load image. Try "Open in new tab".
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: "receipt-viewer-modal" };
</script>

<script setup>
import { ref, computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  receipt: { type: Object, default: null },
});

defineEmits(["close"]);

const imgError = ref(false);

const isImage = computed(() => {
  if (!props.receipt) return false;
  const ct = (props.receipt.content_type || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const name = (props.receipt.original_filename || props.receipt.file_url || "").toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|heic|heif|bmp)(\?|$)/.test(name);
});

const isPdf = computed(() => {
  if (!props.receipt) return false;
  const ct = (props.receipt.content_type || "").toLowerCase();
  if (ct === "application/pdf") return true;
  const name = (props.receipt.original_filename || props.receipt.file_url || "").toLowerCase();
  return /\.pdf(\?|$)/.test(name);
});

function formatBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
</script>
